/**
 * Guided Builder Panel — Concept 1 implementation.
 * Replaces the organisms library in the left panel when Beginner Mode is active.
 * Walks users through 5 steps: Header → Filters → Results → Background → Review.
 */

import { useState, useCallback } from "react";
import { Check, ChevronRight, ChevronLeft, Sparkles, LayoutGrid, Search, Sliders, Palette, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { type LayoutBlockId } from "@/lib/layout-studio/types";

// ── Step definitions ────────────────────────────────────────────────────────

export type GuidedStepId = "header" | "filters" | "results" | "background" | "review";

interface StepOption {
  id: string;
  label: string;
  description: string;
  emoji: string;
  blocks: LayoutBlockId[];
  /** Simple SVG/div preview illustration */
  previewType: "branded" | "search_only" | "full_header" | "filter_full" | "filter_minimal" | "filter_none" | "card_grid" | "list" | "featured" | "bg_white" | "bg_gradient" | "bg_pattern";
}

interface GuidedStep {
  id: GuidedStepId;
  stepNum: number;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  encouragement: string;
  options: StepOption[];
}

const GUIDED_STEPS: GuidedStep[] = [
  {
    id: "header",
    stepNum: 1,
    icon: Search,
    title: "Header",
    subtitle: "Choose how your page starts",
    encouragement: "Let's start at the top! 🎨",
    options: [
      {
        id: "branded",
        label: "Branded Header",
        description: "Logo, title, and search bar",
        emoji: "🏷️",
        blocks: ["header", "search_filters"],
        previewType: "branded",
      },
      {
        id: "search_first",
        label: "Search First",
        description: "Bold search bar as the hero",
        emoji: "🔍",
        blocks: ["search_filters"],
        previewType: "search_only",
      },
      {
        id: "full",
        label: "Full Header",
        description: "Logo, title, search & filters",
        emoji: "✨",
        blocks: ["header", "search_filters", "deal_type_filter"],
        previewType: "full_header",
      },
    ],
  },
  {
    id: "filters",
    stepNum: 2,
    icon: Sliders,
    title: "Filters",
    subtitle: "Help users narrow down deals",
    encouragement: "Great choice! Now let's add filters. 🎯",
    options: [
      {
        id: "full_filters",
        label: "Full Filters",
        description: "Category, deal type & sort",
        emoji: "🗂️",
        blocks: ["deal_type_filter", "sort_bar"],
        previewType: "filter_full",
      },
      {
        id: "essential",
        label: "Essential Only",
        description: "Just sort & deal type",
        emoji: "✅",
        blocks: ["sort_bar"],
        previewType: "filter_minimal",
      },
      {
        id: "none",
        label: "Clean Results",
        description: "No filters, just deals",
        emoji: "🫧",
        blocks: [],
        previewType: "filter_none",
      },
    ],
  },
  {
    id: "results",
    stepNum: 3,
    icon: LayoutGrid,
    title: "Results",
    subtitle: "Pick how deals are displayed",
    encouragement: "Nice! Almost there — let's pick your layout. 🖼️",
    options: [
      {
        id: "card_grid",
        label: "Card Grid",
        description: "3-column grid of deal cards",
        emoji: "⬛",
        blocks: ["results_meta", "results"],
        previewType: "card_grid",
      },
      {
        id: "list_view",
        label: "List View",
        description: "Compact vertical list",
        emoji: "📋",
        blocks: ["results_meta", "results"],
        previewType: "list",
      },
      {
        id: "featured",
        label: "Featured + Grid",
        description: "Hero deals at top + grid",
        emoji: "⭐",
        blocks: ["featured_deals", "results_meta", "results"],
        previewType: "featured",
      },
    ],
  },
  {
    id: "background",
    stepNum: 4,
    icon: Palette,
    title: "Background",
    subtitle: "Set the page atmosphere",
    encouragement: "Looking great! Final touch — the background. 🎨",
    options: [
      {
        id: "clean_white",
        label: "Clean White",
        description: "Crisp, minimal white canvas",
        emoji: "🤍",
        blocks: [],
        previewType: "bg_white",
      },
      {
        id: "soft_gradient",
        label: "Soft Gradient",
        description: "Subtle purple-to-blue fade",
        emoji: "🌅",
        blocks: [],
        previewType: "bg_gradient",
      },
      {
        id: "light_pattern",
        label: "Light Pattern",
        description: "Subtle dot grid texture",
        emoji: "🔲",
        blocks: [],
        previewType: "bg_pattern",
      },
    ],
  },
  {
    id: "review",
    stepNum: 5,
    icon: Eye,
    title: "Review",
    subtitle: "Your layout is ready!",
    encouragement: "",
    options: [],
  },
];

// ── Mini preview illustrations ───────────────────────────────────────────────

function OptionPreview({ type }: { type: StepOption["previewType"] }) {
  const base = "w-full rounded-md overflow-hidden border bg-background";

  switch (type) {
    case "branded":
      return (
        <div className={cn(base, "p-2 space-y-1.5")}>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-sm bg-primary/30" />
            <div className="h-2 w-16 rounded bg-muted-foreground/20" />
          </div>
          <div className="h-5 w-full rounded-sm bg-muted border" />
        </div>
      );
    case "search_only":
      return (
        <div className={cn(base, "p-2 space-y-1")}>
          <div className="h-7 w-full rounded-md bg-primary/10 border border-primary/20 flex items-center px-2">
            <div className="w-2.5 h-2.5 rounded-full border border-muted-foreground/40" />
            <div className="h-1.5 w-16 rounded bg-muted-foreground/20 ml-1.5" />
          </div>
        </div>
      );
    case "full_header":
      return (
        <div className={cn(base, "p-2 space-y-1")}>
          <div className="flex items-center justify-between">
            <div className="w-4 h-4 rounded-sm bg-primary/30" />
            <div className="h-1.5 w-20 rounded bg-muted-foreground/20" />
          </div>
          <div className="h-4 w-full rounded-sm bg-muted border" />
          <div className="flex gap-1">
            <div className="h-2.5 w-10 rounded-full bg-primary/15 border border-primary/20 text-[7px] flex items-center justify-center" />
            <div className="h-2.5 w-10 rounded-full bg-muted border text-[7px]" />
          </div>
        </div>
      );
    case "filter_full":
      return (
        <div className={cn(base, "p-2 space-y-1")}>
          <div className="flex gap-1">
            <div className="h-3 w-12 rounded-full bg-primary/15 border border-primary/20" />
            <div className="h-3 w-10 rounded-full bg-muted border" />
            <div className="h-3 w-8 rounded-full bg-muted border" />
          </div>
          <div className="flex items-center justify-between">
            <div className="h-2 w-16 rounded bg-muted-foreground/20" />
            <div className="h-2 w-12 rounded bg-muted-foreground/15" />
          </div>
        </div>
      );
    case "filter_minimal":
      return (
        <div className={cn(base, "p-2 space-y-1")}>
          <div className="flex items-center justify-between">
            <div className="h-2 w-16 rounded bg-muted-foreground/20" />
            <div className="h-3 w-16 rounded-sm bg-muted border" />
          </div>
        </div>
      );
    case "filter_none":
      return (
        <div className={cn(base, "p-2 flex items-center justify-center")}>
          <div className="h-2 w-24 rounded bg-muted-foreground/15" />
        </div>
      );
    case "card_grid":
      return (
        <div className={cn(base, "p-2 grid grid-cols-3 gap-1")}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-8 rounded-sm bg-muted border" />
          ))}
        </div>
      );
    case "list":
      return (
        <div className={cn(base, "p-2 space-y-1")}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-4 rounded-sm bg-muted border flex items-center px-1.5 gap-1">
              <div className="w-3 h-3 rounded-sm bg-primary/20 flex-shrink-0" />
              <div className="h-1.5 flex-1 rounded bg-muted-foreground/20" />
              <div className="h-1.5 w-6 rounded bg-muted-foreground/10" />
            </div>
          ))}
        </div>
      );
    case "featured":
      return (
        <div className={cn(base, "p-2 space-y-1")}>
          <div className="h-8 rounded-sm bg-primary/10 border border-primary/20" />
          <div className="grid grid-cols-3 gap-1">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-5 rounded-sm bg-muted border" />
            ))}
          </div>
        </div>
      );
    case "bg_white":
      return (
        <div className={cn(base, "p-2 bg-white")}>
          <div className="h-10 rounded-sm border border-border/50 bg-white" />
        </div>
      );
    case "bg_gradient":
      return (
        <div className={cn(base, "p-2 bg-gradient-to-br from-violet-50 to-blue-50")}>
          <div className="h-10 rounded-sm border border-white/60 bg-white/40" />
        </div>
      );
    case "bg_pattern":
      return (
        <div
          className={cn(base, "p-2")}
          style={{ backgroundImage: "radial-gradient(hsl(220 10% 85%) 1px, transparent 1px)", backgroundSize: "8px 8px" }}
        >
          <div className="h-10 rounded-sm border border-border/50 bg-background/80" />
        </div>
      );
  }
}

// ── Step Navigator ───────────────────────────────────────────────────────────

interface StepNavProps {
  steps: GuidedStep[];
  currentStep: GuidedStepId;
  completedSteps: Set<GuidedStepId>;
  onStepClick: (stepId: GuidedStepId) => void;
}

function StepNav({ steps, currentStep, completedSteps, onStepClick }: StepNavProps) {
  return (
    <div className="flex items-center gap-0.5 px-1 py-2">
      {steps.map((step, idx) => {
        const isCompleted = completedSteps.has(step.id);
        const isCurrent = step.id === currentStep;
        const isAccessible = isCompleted || isCurrent || (idx > 0 && completedSteps.has(steps[idx - 1].id));

        return (
          <div key={step.id} className="flex items-center flex-1">
            <button
              className={cn(
                "flex flex-col items-center gap-0.5 flex-1 px-1 py-1 rounded-md transition-all duration-200",
                isCurrent && "bg-primary/10",
                !isAccessible && "opacity-40 cursor-not-allowed",
                isAccessible && !isCurrent && "hover:bg-muted cursor-pointer",
              )}
              onClick={() => isAccessible && onStepClick(step.id)}
              disabled={!isAccessible}
            >
              <div
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300",
                  isCompleted && "bg-green-500 text-white scale-100",
                  isCurrent && !isCompleted && "bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-1",
                  !isCurrent && !isCompleted && "bg-muted text-muted-foreground border border-border",
                )}
              >
                {isCompleted ? <Check className="w-3 h-3" /> : step.stepNum}
              </div>
              <span
                className={cn(
                  "text-[9px] font-medium leading-none",
                  isCurrent ? "text-primary" : "text-muted-foreground",
                )}
              >
                {step.title}
              </span>
            </button>
            {idx < steps.length - 1 && (
              <div
                className={cn(
                  "h-px flex-shrink-0 w-2 mx-0.5 rounded transition-all duration-500",
                  isCompleted ? "bg-green-400" : "bg-border",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Completion Screen ────────────────────────────────────────────────────────

interface CompletionScreenProps {
  selections: Record<GuidedStepId, string | null>;
  onSwitchToAdvanced: () => void;
  onRestart: () => void;
}

function CompletionScreen({ selections, onSwitchToAdvanced, onRestart }: CompletionScreenProps) {
  const getSelectionLabel = (stepId: GuidedStepId) => {
    const step = GUIDED_STEPS.find((s) => s.id === stepId);
    if (!step || step.options.length === 0) return null;
    const option = step.options.find((o) => o.id === selections[stepId]);
    return option ? `${option.emoji} ${option.label}` : null;
  };

  return (
    <div className="flex flex-col h-full items-center justify-center px-4 py-6 animate-fade-in text-center space-y-4">
      <div className="text-4xl animate-scale-in">🎉</div>
      <div>
        <h3 className="font-bold text-base text-foreground">Your page is ready!</h3>
        <p className="text-xs text-muted-foreground mt-1">
          You just built your first layout.
        </p>
      </div>

      <div className="w-full rounded-xl border bg-muted/30 p-3 space-y-2 text-left">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Your choices</p>
        {(["header", "filters", "results", "background"] as GuidedStepId[]).map((sid) => {
          const label = getSelectionLabel(sid);
          const step = GUIDED_STEPS.find((s) => s.id === sid)!;
          if (!label) return null;
          return (
            <div key={sid} className="flex items-center gap-2">
              <step.icon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="text-xs text-foreground">{label}</span>
            </div>
          );
        })}
      </div>

      <div className="w-full space-y-2">
        <Button className="w-full gap-2" size="sm" onClick={onSwitchToAdvanced}>
          <Sparkles className="w-3.5 h-3.5" />
          Continue in Advanced Mode
        </Button>
        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onRestart}>
          Start over
        </Button>
      </div>
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

export interface GuidedBuilderPanelProps {
  onAddBlocks: (blocks: LayoutBlockId[]) => void;
  onComplete: () => void;
}

export function GuidedBuilderPanel({ onAddBlocks, onComplete }: GuidedBuilderPanelProps) {
  const [currentStepId, setCurrentStepId] = useState<GuidedStepId>("header");
  const [selections, setSelections] = useState<Record<GuidedStepId, string | null>>({
    header: null,
    filters: null,
    results: null,
    background: null,
    review: null,
  });
  const [completedSteps, setCompletedSteps] = useState<Set<GuidedStepId>>(new Set());
  const [isComplete, setIsComplete] = useState(false);
  const [lastAdded, setLastAdded] = useState<string | null>(null);

  const currentStep = GUIDED_STEPS.find((s) => s.id === currentStepId)!;
  const currentSelection = selections[currentStepId];
  const hasOptions = currentStep.options.length > 0;
  const canProceed = !hasOptions || currentSelection !== null;

  const handleSelectOption = useCallback((option: StepOption) => {
    setSelections((prev) => ({ ...prev, [currentStepId]: option.id }));
    setLastAdded(option.id);
    setTimeout(() => setLastAdded(null), 600);
  }, [currentStepId]);

  const handleNext = useCallback(() => {
    const stepIdx = GUIDED_STEPS.findIndex((s) => s.id === currentStepId);
    const selectedOption = currentStep.options.find((o) => o.id === currentSelection);

    // Add blocks if an option with blocks was selected
    if (selectedOption && selectedOption.blocks.length > 0) {
      onAddBlocks(selectedOption.blocks);
    }

    // Mark step complete
    setCompletedSteps((prev) => new Set([...prev, currentStepId]));

    const nextStep = GUIDED_STEPS[stepIdx + 1];
    if (!nextStep || nextStep.id === "review") {
      setIsComplete(true);
    } else {
      setCurrentStepId(nextStep.id);
    }
  }, [currentStepId, currentSelection, currentStep.options, onAddBlocks]);

  const handleBack = useCallback(() => {
    const stepIdx = GUIDED_STEPS.findIndex((s) => s.id === currentStepId);
    if (stepIdx > 0) {
      setCurrentStepId(GUIDED_STEPS[stepIdx - 1].id);
    }
  }, [currentStepId]);

  const handleRestart = useCallback(() => {
    setCurrentStepId("header");
    setSelections({ header: null, filters: null, results: null, background: null, review: null });
    setCompletedSteps(new Set());
    setIsComplete(false);
  }, []);

  if (isComplete) {
    return (
      <CompletionScreen
        selections={selections}
        onSwitchToAdvanced={onComplete}
        onRestart={handleRestart}
      />
    );
  }

  const currentStepIdx = GUIDED_STEPS.findIndex((s) => s.id === currentStepId);

  return (
    <div className="flex flex-col h-full">
      {/* Step Navigator */}
      <div className="border-b px-2">
        <StepNav
          steps={GUIDED_STEPS}
          currentStep={currentStepId}
          completedSteps={completedSteps}
          onStepClick={setCurrentStepId}
        />
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-auto p-3 space-y-3">
        {/* Encouragement */}
        <div className="space-y-0.5">
          <div className="text-xs font-semibold text-primary flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            {currentStep.encouragement || currentStep.subtitle}
          </div>
          <p className="text-xs text-muted-foreground">{currentStep.subtitle}</p>
        </div>

        {/* Options */}
        {currentStep.options.map((option) => {
          const isSelected = currentSelection === option.id;
          const wasJustAdded = lastAdded === option.id;
          return (
            <button
              key={option.id}
              className={cn(
                "w-full rounded-xl border-2 p-3 text-left transition-all duration-200 space-y-2 group",
                isSelected
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-card hover:border-primary/40 hover:bg-accent/30",
                wasJustAdded && "scale-[0.98]",
              )}
              onClick={() => handleSelectOption(option)}
            >
              {/* Option Preview */}
              <div
                className={cn(
                  "transition-all duration-300",
                  isSelected ? "opacity-100" : "opacity-70 group-hover:opacity-90",
                )}
              >
                <OptionPreview type={option.previewType} />
              </div>

              {/* Option Label */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">{option.emoji}</span>
                    <span className="text-xs font-semibold text-foreground">{option.label}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{option.description}</p>
                </div>
                {isSelected && (
                  <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5 animate-scale-in">
                    <Check className="w-2.5 h-2.5 text-primary-foreground" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Navigation Buttons */}
      <div className="border-t p-3 flex gap-2">
        {currentStepIdx > 0 && (
          <Button variant="ghost" size="sm" className="flex-none" onClick={handleBack}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
        )}
        <Button
          size="sm"
          className="flex-1 gap-1.5"
          disabled={!canProceed}
          onClick={handleNext}
        >
          {currentStepIdx < GUIDED_STEPS.length - 2 ? (
            <>
              Next Step
              <ChevronRight className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5" />
              Finish Setup
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
